/**
 * API Service Layer for PrintBox3D Frontend
 *
 * Centralised HTTP layer that delegates all requests to apiClient (which
 * handles JWT injection, automatic token refresh on 401, and error
 * normalisation). The cache layer above it is unchanged.
 *
 * Usage:
 *   import { productAPI, orderAPI, authAPI } from './services/api';
 *   const products = await productAPI.getAll();
 */

import API_BASE_URL from '../config/api';
import apiClient from './apiClient';

// =============================================================================
// CACHE MANAGEMENT
// =============================================================================
const cache = {
  data: new Map(),
  timestamps: new Map(),
  TTL: 5 * 60 * 1000, // 5 minutes

  set(key, value) {
    this.data.set(key, value);
    this.timestamps.set(key, Date.now());
  },

  get(key) {
    const ts = this.timestamps.get(key);
    if (!ts) return null;
    if (Date.now() - ts > this.TTL) {
      this.data.delete(key);
      this.timestamps.delete(key);
      return null;
    }
    return this.data.get(key);
  },

  clear() {
    this.data.clear();
    this.timestamps.clear();
  },
};

// =============================================================================
// PRODUCT API
// =============================================================================
export const productAPI = {
  getAll: async (filters = {}) => {
    const cacheKey = `products_${JSON.stringify(filters)}`;
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    const buildQuery = (page) => {
      const queryParams = new URLSearchParams();
      if (filters.category && filters.category !== 'all') {
        queryParams.append('category__slug', filters.category);
      }
      if (filters.material && filters.material !== 'all') {
        queryParams.append('material__name', filters.material);
      }
      if (filters.sort) {
        const sortMap = {
          'price-low': 'price',
          'price-high': '-price',
          'name': 'name',
          'featured': '-is_featured,-created_at',
        };
        queryParams.append('ordering', sortMap[filters.sort] || '-is_featured,-created_at');
      }
      if (filters.customOnly) queryParams.append('requires_custom_name', 'true');
      if (filters.search) queryParams.append('search', filters.search);
      if (page) queryParams.append('page', String(page));
      const qs = queryParams.toString();
      return `/api/products/${qs ? `?${qs}` : ''}`;
    };

    const firstPage = await apiClient.get(buildQuery(1));
    if (Array.isArray(firstPage)) {
      if (firstPage.length > 0) cache.set(cacheKey, firstPage);
      return firstPage;
    }

    let results = [...(firstPage.results || [])];
    let nextUrl = firstPage.next;
    let page = 2;

    while (nextUrl) {
      const data = await apiClient.get(buildQuery(page));
      results = results.concat(data.results || []);
      nextUrl = data.next;
      page += 1;
    }

    if (results.length > 0) cache.set(cacheKey, results);
    return results;
  },

  getBySlug: async (slug) => {
    const cacheKey = `product_${slug}`;
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    const data = await apiClient.get(`/api/products/${slug}/`);
    cache.set(cacheKey, data);
    return data;
  },

  getRelated: async (slug) => {
    const data = await apiClient.get(`/api/products/${slug}/related/`);
    return Array.isArray(data) ? data : (data.results || []);
  },

  getFeatured: async () => {
    const cacheKey = 'products_featured';
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    const data = await apiClient.get('/api/products/featured/');
    const results = Array.isArray(data) ? data : (data.results || []);
    if (results.length > 0) cache.set(cacheKey, results);
    return results;
  },

  getBestSellers: async () => {
    const cacheKey = 'products_bestsellers';
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    const data = await apiClient.get('/api/products/best_sellers/');
    const results = Array.isArray(data) ? data : (data.results || []);
    if (results.length > 0) cache.set(cacheKey, results);
    return results;
  },
};

// =============================================================================
// CONTACT API
// =============================================================================
export const contactAPI = {
  submit: async (formData) => apiClient.post('/api/contact/', formData),
};

// =============================================================================
// CUSTOM ORDER API
// =============================================================================
export const customOrderAPI = {
  // FormData (multipart) — send raw fetch so browser sets multipart boundary
  submit: async (formData) => {
    const response = await fetch(`${API_BASE_URL}/api/custom-orders/`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to submit custom order');
    }
    return response.json();
  },
};

// =============================================================================
// SURPRISE (AD CAMPAIGN) API
// =============================================================================
export const surpriseAPI = {
  submit: async (data) => apiClient.post('/api/surprise/', data),
};

// =============================================================================
// CATEGORIES API
// =============================================================================
export const categoryAPI = {
  getAll: async () => {
    const data = await apiClient.get('/api/categories/');
    return data.results || data;
  },

  getBySlug: async (slug) => apiClient.get(`/api/categories/${slug}/`),
};

// =============================================================================
// NEWSLETTER API
// =============================================================================
export const newsletterAPI = {
  subscribe: async (email) => apiClient.post('/api/newsletter/', { email }),
};

// =============================================================================
// TESTIMONIALS API
// =============================================================================
export const testimonialAPI = {
  getAll:    async () => apiClient.get('/api/testimonials/'),
  getFeatured: async () => apiClient.get('/api/testimonials/featured/'),
};

// =============================================================================
// AUTH API
// =============================================================================
export const authAPI = {
  register: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/register/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const ct = response.headers.get('content-type') || '';
      const errorData = ct.includes('application/json')
        ? await response.json()
        : { detail: 'Registration endpoint not available.' };
      const err = new Error('Registration failed');
      err.status = response.status;
      err.data = errorData;
      throw err;
    }

    const data = await response.json();
    const accessToken  = data.tokens?.access  || data.token;
    const refreshToken = data.tokens?.refresh;
    if (accessToken && data.user) apiClient.saveTokens(accessToken, refreshToken, data.user);
    return data;
  },

  login: async (credentials) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const ct = response.headers.get('content-type') || '';
      const errorData = ct.includes('application/json')
        ? await response.json()
        : { detail: 'Login endpoint not available.' };
      const err = new Error('Login failed');
      err.status = response.status;
      err.data = errorData;
      throw err;
    }

    const data = await response.json();
    const accessToken  = data.tokens?.access  || data.token;
    const refreshToken = data.tokens?.refresh;
    if (accessToken) apiClient.saveTokens(accessToken, refreshToken, data.user);
    return data;
  },

  logout: () => {
    apiClient.clearTokens();
    cache.clear();
  },

  getCurrentUser: async () => {
    try {
      const user = await apiClient.get('/api/auth/profile/');
      localStorage.setItem('user', JSON.stringify(user));
      sessionStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch {
      authAPI.logout();
      return null;
    }
  },

  updateProfile: async (userData) => {
    const data = await apiClient.put('/api/auth/profile/update/', userData);
    const user = data.user ?? data;
    localStorage.setItem('user', JSON.stringify(user));
    sessionStorage.setItem('user', JSON.stringify(user));
    return user;
  },

  forgotPassword: async (email) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = data.error || data.detail || 'Could not send reset email. Please try again.';
      const err = new Error(message);
      err.data = data;
      throw err;
    }
    return data;
  },

  resetPassword: async (uid, token, new_password) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/reset-password/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, token, new_password }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = data.error || data.detail || 'Password reset failed. Please try again.';
      const err = new Error(message);
      err.data = data;
      throw err;
    }
    return data;
  },

  getOrders: async () => apiClient.get('/api/orders/user/me/'),

  /**
   * Google Sign-In — sends the ID token to the backend for server-side
   * verification. The backend checks the signature, expiry, and audience
   * before issuing our own JWT.  We never decode the Google token here.
   */
  googleLogin: async (credential) => {
    const response = await fetch(`${API_BASE_URL}/api/auth/google/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
    });

    if (!response.ok) {
      const ct = response.headers.get('content-type') || '';
      const errorData = ct.includes('application/json')
        ? await response.json()
        : { detail: 'Google Sign-In endpoint not available.' };
      const err = new Error('Google Sign-In failed');
      err.status = response.status;
      err.data = errorData;
      throw err;
    }

    const data = await response.json();
    const accessToken  = data.tokens?.access  || data.token;
    const refreshToken = data.tokens?.refresh;
    if (accessToken) apiClient.saveTokens(accessToken, refreshToken, data.user);
    return data;
  },
};

// =============================================================================
// CART / COMBOS / SHIPPING API
// =============================================================================
export const GIFT_WRAP_PRICE = 79;

export const EXPRESS_SURCHARGE_PERCENT = 30;

export const cartTotalsAPI = {
  calculate: async (
    items,
    couponCode = '',
    giftWrap = false,
    deliveryMethod = 'SURFACE',
    pincode = '',
    comboBoxVariants = {},
  ) =>
    apiClient.post('/api/cart/calculate/', {
      items,
      coupon_code: couponCode,
      gift_wrap: giftWrap,
      delivery_method: deliveryMethod,
      pincode,
      combo_box_variants: comboBoxVariants,
    }),
};

export const reviewAPI = {
  getForProduct: async (slug) => apiClient.get(`/api/products/${slug}/reviews/`),
  submit: async (slug, { rating, comment }) =>
    apiClient.post(`/api/products/${slug}/reviews/`, { rating, comment }),
};

export const combosAPI = {
  getAll: async () => apiClient.get('/api/combos/'),
};

export const shippingAPI = {
  getSettings: async () => apiClient.get('/api/shipping/settings/'),
  getEstimate: async (pincode, deliveryMethod = 'SURFACE') =>
    apiClient.get(`/api/shipping/estimate/?pincode=${encodeURIComponent(pincode)}&delivery_method=${deliveryMethod}`),
};

// =============================================================================
// ADMIN PRODUCT API (staff only)
// =============================================================================
export const adminProductAPI = {
  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.page) queryParams.append('page', String(filters.page));
    const qs = queryParams.toString();

    const firstPage = await apiClient.get(`/api/admin/products/${qs ? `?${qs}` : ''}`);
    if (Array.isArray(firstPage)) return firstPage;

    let results = [...(firstPage.results || [])];
    let nextUrl = firstPage.next;
    let page = 2;

    while (nextUrl) {
      const data = await apiClient.get(`/api/admin/products/?page=${page}${filters.search ? `&search=${encodeURIComponent(filters.search)}` : ''}`);
      results = results.concat(data.results || []);
      nextUrl = data.next;
      page += 1;
    }

    return results;
  },

  getById: async (id) => apiClient.get(`/api/admin/products/${id}/`),

  create: async (payload) => {
    cache.clear();
    return apiClient.post('/api/admin/products/', payload);
  },

  update: async (id, payload) => {
    cache.clear();
    return apiClient.patch(`/api/admin/products/${id}/`, payload);
  },

  delete: async (id) => {
    cache.clear();
    return apiClient.delete(`/api/admin/products/${id}/`);
  },
};

// Legacy backend cart endpoints (unused — cart is client-side)
export const cartAPI = {
  getCart:    () => apiClient.get('/api/cart/'),
  addItem:    (productId, quantity = 1) => apiClient.post('/api/cart/add/', { product_id: productId, quantity }),
  updateItem: (itemId, quantity)         => apiClient.put(`/api/cart/update/${itemId}/`, { quantity }),
  removeItem: (itemId)                   => apiClient.delete(`/api/cart/remove/${itemId}/`),
  clearCart:  ()                         => apiClient.delete('/api/cart/clear/'),
};

// =============================================================================
// ORDER / PAYMENT API
// =============================================================================
export const orderAPI = {
  createOrder:         (orderData)    => apiClient.post('/api/orders/create/', orderData),
  verifyPayment:       (paymentData)  => apiClient.post('/api/orders/verify-payment/', paymentData),
  getOrderStatus:      (orderId)      => apiClient.get(`/api/orders/${orderId}/`),
  recordPaymentFailure: (data)        => apiClient.post('/api/orders/payment-failed/', data),
};

// =============================================================================
// DEFAULT EXPORT — thin wrappers for backward compatibility
// =============================================================================
const api = {
  get:    (url) => apiClient.get(url),
  post:   (url, data) => apiClient.post(url, data),
  put:    (url, data) => apiClient.put(url, data),
  delete: (url) => apiClient.delete(url),
};

export default api;
